import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MainLayout from '../components/layout/MainLayout';
import PointageModule from '../components/shared/PointageModule';
import MarquerFinJournee from '../components/MarquerFinJournee';
import PersonnelManagementModule from '../components/shared/PersonnelManagementModule';
import { ProjetsModule } from '../components/projets';
import { AgendaModule, EmployesModule, TDBEmployesModule } from '../components/comptabilite';
import { TaskManagementModule } from '../components/rh';
import MaPerformance from '../components/rh/MaPerformance';
import PreuvesPanel from '../components/shared/PreuvesPanel';
import ObjectifsEmployesModule from '../components/shared/ObjectifsEmployesModule';
import MesDemandesPermission from '../components/employe/MesDemandesPermission';
import useDeptDashboard from '../hooks/useDeptDashboard';
import TaskToggleWithPreuve from '../components/shared/TaskToggleWithPreuve'; // eslint-disable-line no-unused-vars
import api from '../services/api';
import './DashboardTechnologiePage.css';

const DEPT_ID = 16;

const PRIORITE_COLOR = { urgente: '#ef4444', haute: '#f97316', normale: '#3b82f6', basse: '#22c55e' };

function MesTaches({ userId }) {
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtre, setFiltre] = useState('tout');

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.tasks.getAll({ employee_id: userId })
      .then(data => setTaches(Array.isArray(data) ? data : (data.tasks || data.data || [])))
      .catch(() => setTaches([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleToggle = async (id) => {
    try {
      await api.tasks.toggle(id);
      setTaches(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
    } catch (_) {}
  };

  const tachesFiltrees = taches.filter(t => {
    if (filtre === 'todo') return !t.done;
    if (filtre === 'done') return t.done;
    return true;
  });

  return (
    <div className="mes-taches">
      <div className="mes-taches-header">
        <h3 className="mes-taches-title">🔹 Mes T�ches</h3>
        <div className="mes-taches-filtres">
          {['tout', 'todo', 'done'].map(f => (
            <button key={f} onClick={() => setFiltre(f)} className={`filtre-btn${filtre === f ? ' active' : ''}`}>
              {f === 'tout' ? 'Toutes' : f === 'todo' ? '� faire' : 'Termin�es'}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="taches-empty">Chargement�</div>
      ) : tachesFiltrees.length === 0 ? (
        <div className="taches-empty">Aucune t�che</div>
      ) : (
        <div className="taches-list">
          {tachesFiltrees.map(t => {
            const done = t.done;
            const prio = (t.priority || '').toLowerCase();
            return (
              <div key={t.id} className={`tache-item${done ? ' done' : ''}`}>
                <button onClick={() => handleToggle(t.id)} className={`tache-check${done ? ' checked' : ''}`}>
                  {done ? '?' : ''}
                </button>
                <div className="tache-body">
                  <div className={`tache-titre${done ? ' done' : ''}`}>{t.titre}</div>
                  {t.description && <div className="tache-desc">{t.description}</div>}
                  <div className="tache-meta">
                    {prio && (
                      <span className="tache-prio" style={{ background: `${PRIORITE_COLOR[prio] || '#667'}22`, color: PRIORITE_COLOR[prio] || '#667' }}>
                        {prio}
                      </span>
                    )}
                    {t.deadline && <span className="tache-date">🔹 {new Date(t.deadline).toLocaleDateString('fr-FR')}</span>}
                    {t.project && <span className="tache-projet">🔹 {t.project.nom || t.project.name}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DashboardTechnologiePage() {
  const navigate = useNavigate();
  const { logout: authLogout, user } = useAuth();
  const [activePage, setActivePage] = useState('projets');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { employes, setEmployes, tasks, loading, error, refetch } =
    useDeptDashboard({ deptId: DEPT_ID, withProjects: true });

  const allObj = employes.flatMap(e => e.objectifs || []);
  const totalTasks = employes.reduce((sum, emp) => sum + (emp.assignedTasks || []).length, 0);
  const handleLogout = async () => { await authLogout(); navigate('/auth', { replace: true }); };

  const handleNav = (id) => { setActivePage(id); setSidebarOpen(false); };

  const NAV_ITEMS = [
    { id: 'projets', icon: '🔹', label: 'Projets Tech' },
    { id: 'mes-taches', icon: '🔹', label: 'Mes Tâches' },
    { id: 'ma-performance', icon: '⭐', label: 'Ma Performance' },
    { id: 'permissions', icon: '📝', label: 'Mes Permissions' },
    { id: 'objectifs', icon: '🎯', label: 'Objectifs', badge: allObj.filter(o => o.statut === 'atteint').length || null },
    { id: 'personnel', icon: '🔹', label: 'Personnel', badge: employes.length },
    { id: 'agenda', icon: '🔹', label: 'Planning & Sprints' },
    { id: 'gestion-taches', icon: '🔹', label: 'Gestion Tâches', badge: totalTasks || null },
    { id: 'employes', icon: '🗑️🔹', label: 'Equipe Dev' },
    { id: 'rapports', icon: '🔹', label: 'Rapports Tech' },
    { id: 'documents', icon: '📁', label: 'Documents' },
    { id: 'tdb-employes', icon: '📊', label: 'Performance Dev' },
  ];

  const renderModule = () => {
    switch (activePage) {
      case 'projets': return <ProjetsModule deptId={DEPT_ID} />;
      case 'mes-taches': return <MesTaches userId={user?.id} />;
      case 'ma-performance': return (
        <div style={{ padding: '0 20px' }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: '#fff' }}>⭐ Ma Performance</h2>
            <p style={{ fontSize: 13, color: '#888' }}>Consultez votre performance personnelle</p>
          </div>
          <MaPerformance />
        </div>
      );
      case 'permissions': return <MesDemandesPermission />;
      case 'objectifs': return (
        <div style={{ padding: '0 20px' }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: '#fff' }}>🎯 Objectifs de l'Équipe</h2>
            <p style={{ fontSize: 13, color: '#889', margin: 0 }}>Suivi par collaborateur · Cocher les objectifs atteints</p>
          </div>
          <ObjectifsEmployesModule employes={employes} onRefresh={refetch} isChef={true} />
        </div>
      );
      case 'personnel': return <PersonnelManagementModule employes={employes} setEmployes={setEmployes} departmentName="Technologie & Développement" heureLimite="09:30" deptId={DEPT_ID} />;
      case 'agenda': return <AgendaModule />;
      case 'gestion-taches': return <><PreuvesPanel tasks={tasks} employes={employes} /><TaskManagementModule employes={employes} setEmployes={setEmployes} /></>;
      case 'employes': return <EmployesModule />;
      case 'rapports': return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}><h3>🔹 Rapports Technologie</h3><p>Disponible prochainement.</p></div>;
      case 'documents': navigate('/documents'); return null;
      case 'tdb-employes': return <TDBEmployesModule />;
      default: return <ProjetsModule deptId={DEPT_ID} />;
    }
  };

  return (
    <MainLayout>
      <div className="dept-dashboard">

        {/* TOPBAR */}
        <div className="dept-topbar">
          <div className="dept-topbar-brand">
            <button className="dept-menu-toggle" onClick={() => setSidebarOpen(true)}>?</button>
            <div className="dept-topbar-logo">🔹</div>
            <div>
              <div className="dept-topbar-title">FHC Groupe SARL</div>
              <div className="dept-topbar-subtitle">Chef D�partement Technologie</div>
            </div>
          </div>
          <div className="dept-topbar-actions">
            {loading && <span style={{ fontSize: 11, color: '#C9A84C' }}>Chargement�</span>}
            {error && <span style={{ fontSize: 11, color: '#ef8080' }}>🔹 {error}</span>}
            <div className="dept-topbar-date">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <button onClick={refetch} className="dept-topbar-btn">?</button>
            <button onClick={handleLogout} className="dept-topbar-btn-logout">D�connexion</button>
          </div>
        </div>

        {/* LAYOUT */}
        <div className="dept-layout">

          {/* Overlay mobile */}
          <div className={`dept-sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />

          {/* SIDEBAR */}
          <div className={`dept-sidebar${sidebarOpen ? ' open' : ''}`}>
            <div className="dept-sidebar-section-title">MODULES</div>
            {NAV_ITEMS.map(item => (
              <div key={item.id} onClick={() => handleNav(item.id)}
                className={`dept-sidebar-item${activePage === item.id ? ' active' : ''}`}>
                <span className="dept-sidebar-item-icon">{item.icon}</span>
                {item.label}
                {item.badge > 0 && <span className="dept-sidebar-badge">{item.badge}</span>}
              </div>
            ))}
            <div className="dept-sidebar-divider" />
            <div className="dept-sidebar-section-title">ACTIONS</div>
            {[{ icon: '?', label: 'Nouveau Projet' }, { icon: '🔹', label: 'D�ployer' }, { icon: '🔹', label: 'Exporter' }].map(item => (
              <div key={item.label} className="dept-sidebar-item">
                <span className="dept-sidebar-item-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>

          {/* MAIN */}
          <div className="dept-main">
            <PointageModule currentUser={{ nom: 'Chef Technologie' }} />
            <MarquerFinJournee onSuccess={() => {
              console.log('Fin de journée enregistrée - Dashboard Technologie');
            }} />
            {renderModule()}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default DashboardTechnologiePage;
